import { ChangeDetectorRef, Component, Input, OnDestroy, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { PetlService } from '../../../services/petl.service';
import { PetlBaseComponent } from '../petl-base.component';
import { IColumnResult, IDatabaseField } from '../petl-types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-petl-column-mapping-ng',
    styleUrls: ['../../sharedStyles/base.scss', '../petl-base.scss', './column-mapping.component.scss'],
    templateUrl: './column-mapping.component.html'
})

export class PetlColumnMappingComponent extends PetlBaseComponent implements OnDestroy {

    geometryType?: string = null;
    fileUploadedSubscription: Subscription;
    standardFields?: IDatabaseField[];
    draggedDatabaseField: any = null;
    dataItemInEditMode?: IColumnResult;
    draggedColumn: any = null;
    showHelp: false;

    constructor(_vcr: ViewContainerRef, protected readonly petlService: PetlService, protected readonly cdr: ChangeDetectorRef) {
        super(_vcr, petlService, cdr);
        this.refreshColumnMapping();
        this.fileUploadedSubscription = this.petlService.fileUploaded.subscribe(() => {
            this.refreshColumnMapping();
        });
    }

    ngOnDestroy() {
        this.fileUploadedSubscription.unsubscribe();
    }

    get allColumnsMapped() {
        return this.petlService.columnData && this.getNumberOfColumnsMapped() === this.petlService.columnData.length;
    }

    get databaseFieldsNotCustom() {
        let results = this.filterColumns(this.petlService.results.unusedDatabaseFields || [], 'customField', false);
        results = this.filterColumns(results, 'hidden', false);
        return results;
    }

    get databaseFieldsCustom() {
        let results = this.filterColumns(this.petlService.results.unusedDatabaseFields || [], 'customField', true);
        results = this.filterColumns(results, 'hidden', false);
        results = results.sort((a, b) => this.customFieldComparator(a, b));
        const filteredResults = [];
        const limit = 1; // Only display 1 of eFach unused custom field type
        const counts = new Array(this.petlService.customTypes.length).fill(0);
        for (const result of results) {
            const prefix = result.databaseName.split('_')[0];
            const index = this.petlService.customTypes.indexOf(prefix);
            const count = counts[index]++;
            if (count < limit) {
                if (result.displayName.match(/\d/)) {
                    result.displayName = result.displayName.split(' ').slice(0, -1).join(' ');
                }
                filteredResults.push(result);
            }
        }
        return filteredResults;
    }

    get noColumnsMapped() {
        return this.getNumberOfColumnsMapped() === 0;
    }

    get sheetsForSelect() {
        return this.populateArray(this.petlService.sheetCount, 1);
    }

    get geometryClass() {
        if (this.geometryIsValid()) {
            return 'label-success';
        } else {
            return 'label-danger';
        }
    }

    get service() {
        return this.petlService;
    }

    @Input()
    geometryIsValid = () => {
        return this.geometryType && this.geometryType !== 'Unknown';
    }

    @Input()
    public initialize = () => {
        this.draggedDatabaseField = null;
        this.draggedColumn = null;
        this.initializeStandardFieldsForHelp();
    }

    @Input()
    public refresh = () => {
        this.refreshColumnMapping();
    }

    async clearPreviousMappingAndResetColumnMapping() {
        this.petlService.showRepeatFileNameIndicator = false;
        this.petlService.ignorePreviousColumnMappings = true;
        await this.resetAutomatedColumnMapping();
    }

    async resetAutomatedColumnMapping() {
        this.removeAllMappedFields();
        await this.petlService.resetAutomatedColumnMapping();
        this.refreshColumnMapping();
    }

    async redefineStartingRowNumber() {
        await this.service.redefineStartingRowNumber();
    }

    initializeStandardFieldsForHelp() {
        const twoChar = 2;
        const fourChar = 4;
        this.standardFields = [];
        this.addStandardField('County', twoChar);
        this.addStandardField('SR', fourChar);
        this.addStandardField('Seg Begin', fourChar);
        this.addStandardField('Seg End', fourChar);
        this.addStandardField('Jurisdiction', 1);
        this.addStandardField('Offset Begin');
        this.addStandardField('Offset End');
        this.addStandardField('Seg Length');
        this.addStandardField('Lat');
        this.addStandardField('Long');
        this.addStandardField('BRKey');
        this.addStandardField('Bridge Id');
        this.addStandardField('MPMS Number');
    }

    addStandardField(name, maxlength: number = null) {
        const standardField: IDatabaseField = {};
        standardField.displayName = name;
        if (maxlength) {
            standardField.maxLength = maxlength;
        }
        this.standardFields.push(standardField);
        this.standardFields.push();
    }

    columnMappingClick(ev: any) {
        if (!ev.path || !ev.path.indexOf('edit-alias')) {
            this.closeEditMode();
        }
    }

    dataItemClick(ev: Event, dataItem: IColumnResult) {
        ev.stopPropagation();
        if (this.dataItemInEditMode && this.dataItemInEditMode !== dataItem) {
            this.dataItemInEditMode.editMode = false;
        }
        this.dataItemInEditMode = dataItem;
        this.dataItemInEditMode.editMode = true;
    }

    inputBlur() {
        this.closeEditMode();
    }

    closeEditMode() {
        if (this.dataItemInEditMode) {
            this.dataItemInEditMode.editMode = false;
            this.dataItemInEditMode.displayName = this.getDragElementDisplayValue(this.dataItemInEditMode);
            this.dataItemInEditMode = null;
        }
    }

    getDatabaseFieldClass(databaseField: IDatabaseField) {
        if (databaseField.customField) {
            return 'alert-info';
        } else {
            return 'alert-success';
        }
    }

    getColumnClass(data) {
        if (data.databaseField) {
            return this.getDatabaseFieldClass(data.databaseField);
        }
        return 'empty';
    }

    getColumnElementByIndex(index) {
        return this.petlService.columnData.filter((x) => x.index === index)[0];
    }

    getIndexOfDatabaseFieldInUnusedList(propertyName) {
        return this.petlService.results.unusedDatabaseFields.map((x) => x.propertyName).indexOf(propertyName);
    }

    setDraggedDatabaseField(databaseField, columnDataElement) {
        this.draggedDatabaseField = databaseField;
        this.draggedColumn = columnDataElement;
    }

    getDraggedDatabaseField() {
        return this.draggedDatabaseField;
    }

    setColumnDataElementDroppedOn(columnDataElementDroppedOn) {
        this.petlService.mappingsFromServerAltered = true;
        const columnDroppedOn = this.getColumnElementByIndex(columnDataElementDroppedOn.index);
        if (this.draggedColumn && columnDroppedOn && this.draggedColumn.index === columnDroppedOn.index) {
            return; // do nothing, dropped onto itself
        }
        if (columnDroppedOn.databaseField && columnDroppedOn.databaseField.propertyName !== this.draggedDatabaseField.propertyName) {
            const droppedOnDatabaseField = columnDroppedOn.databaseField;
            columnDroppedOn.databaseField = this.draggedDatabaseField;
            this.moveDatabaseFieldToUnusedList(droppedOnDatabaseField);
        } else if (!columnDroppedOn.databaseField) {
            // Field was dragged to a column that was not mapped
            columnDroppedOn.databaseField = this.draggedDatabaseField;
        }
        if (columnDroppedOn.databaseField && columnDroppedOn.databaseField.customField && columnDataElementDroppedOn.nativeHeader) {
            columnDroppedOn.databaseField.aliasName = columnDataElementDroppedOn.nativeHeader;
        }

        if (this.draggedColumn) {
            // A mapped field was dragged from one column to another column
            this.clearDraggedColumn();
        } else {
            // Database field was from the unused list, remove the database field dropped from the unused list
            this.removeDatabaseFieldFromUnusedList(this.draggedDatabaseField);
        }

        this.refreshColumnMapping();
    }

    setGeometryType() {
        if (!this.petlService.columnMapping) {
            this.geometryType = null;
            return;
        }

        if (this.isLine()) {
            this.geometryType = 'Line';
        } else if (this.isPoint()) {
            this.geometryType = 'Point';
        } else if (this.isBridge()) {
            this.geometryType = 'Bridge';
        } else if (this.isMpms()) {
            this.geometryType = 'MPMS';
        } else {
            this.geometryType = 'Unknown';
        }
    }

    removeDatabaseFieldFromUnusedList(databaseField: IDatabaseField) {
        const databaseFieldIndex = this.getIndexOfDatabaseFieldInUnusedList(databaseField.propertyName);
        this.petlService.results.unusedDatabaseFields.splice(databaseFieldIndex, 1);
        this.updateHiddenFields();
    }

    moveDatabaseFieldToUnusedList(databaseField: IDatabaseField) {
        if (!databaseField) {
            databaseField = this.draggedDatabaseField;
        }
        if (databaseField && this.getIndexOfDatabaseFieldInUnusedList(databaseField.propertyName) === -1) {
            databaseField.aliasName = null;
            this.petlService.results.unusedDatabaseFields.push(databaseField);
            this.updateHiddenFields();
            // Sort the unused list by databaseName
            this.sortUnusedDatabaseFields();
        }
        this.clearDraggedColumn();
        this.refreshColumnMapping();
    }

    updateHiddenFields() {
        for (const column of this.petlService.columnData) {
            if (column.databaseField && column.databaseField.hidesOtherField) {
                this.removeFieldFromMapping(column.databaseField.hidesOtherField);
                this.toggleUnusedField(column.databaseField.hidesOtherField, true);
            }
        }
        for (const unusedField of this.petlService.results.unusedDatabaseFields) {
            if (unusedField.hidesOtherField) {
                this.toggleUnusedField(unusedField.hidesOtherField, false);
            }
        }
    }

    refreshColumnMapping() {
        if (this.petlService.columnData) {
            this.resetColumnMapping();
            this.setNewColumnMapping();
            this.updateHiddenFields();
            this.setGeometryType();
            this.setDraggedDisplayNames();
        }
    }

    removeFieldFromMapping(propertyName) {
        for (const column of this.petlService.columnData) {
            if (column.databaseField && column.databaseField.propertyName === propertyName) {
                column.databaseField.aliasName = null;
                this.petlService.results.unusedDatabaseFields.push(column.databaseField);
                column.databaseField = null;
                return true;
            }
        }
        return false;
    }

    toggleUnusedField(propertyName, hide) {
        for (const unusedField of this.petlService.results.unusedDatabaseFields) {
            if (unusedField.propertyName === propertyName) {
                unusedField.hidden = hide;
                return true;
            }
        }
        return false;
    }

    sortUnusedDatabaseFields() {
        this.petlService.results.unusedDatabaseFields.sort((a, b) => {
            if (!a.customField && a.customField !== null && a.customField !== b.customField) {
                { return -1; }
            } else if (a.customField && a.customField !== b.customField) {
                return 1;
            } else if (a.databaseName < b.databaseName) {
                return -1;
            } else if (a.databaseName > b.databaseName) {
                return 1;
            }
            return 0;
        });
    }

    clearDraggedColumn() {
        if (this.draggedColumn) {
            const sourceColumn = this.getColumnElementByIndex(this.draggedColumn.index);
            sourceColumn.databaseField = null;
        }
    }

    resetColumnMapping() {
        for (const key of Object.keys(this.petlService.columnMapping)) {
            this.petlService.columnMapping[key] = null;
        }
    }

    setNewColumnMapping() {
        this.petlService.columnData.forEach((column, i) => {
            if (column.databaseField) {
                const databaseField = column.databaseField;
                this.petlService.columnMapping[databaseField.javaScriptPropertyName] = i;
            }
        });
    }

    setDraggedDisplayNames() {
        for (const columnData of this.petlService.columnData) {
            columnData.displayName = this.getDragElementDisplayValue(columnData);
        }
    }

    isPoint() {
        const pointProperties = ['county', 'stateRoute', 'segmentBegin', 'offsetBegin'];
        const xAndyProperties = ['xValue', 'yValue'];
        return !this.mappingHasValue('segmentLength') && (this.mappingHasAllProperties(pointProperties) || this.mappingHasAllProperties(xAndyProperties));
    }

    isLine() {
        const lineProperties = ['county', 'stateRoute', 'segmentBegin'];
        const functionLocationLineProperties = ['functionalLocation', 'segmentBegin'];
        const orgLineProperties = ['orgCode', 'stateRoute', 'segmentBegin'];
        if ((this.mappingHasAllProperties(lineProperties) || this.mappingHasAllProperties(functionLocationLineProperties) || this.mappingHasAllProperties(orgLineProperties))
            && !(this.mappingHasValue('offsetBegin') && !this.mappingHasValue('segmentEnd') && !this.mappingHasValue('offsetEnd'))
            && !(this.mappingHasValue('segmentLength') && (this.mappingHasValue('segmentEnd') || this.mappingHasValue('offsetEnd')))) {
            return true;
        }
        return false;
    }

    isMpms() {
        return this.mappingHasValue('mpmsNumber');
    }

    isBridge() {
        return this.mappingHasValue('brKey') || this.mappingHasValue('bridgeId');
    }

    mappingHasAllProperties(properties) {
        let allFound = true;
        for (const property of properties) {
            allFound = allFound && this.mappingHasValue(property);
        }
        return allFound;
    }

    mappingHasValue(property) {
        return this.hasVal(this.petlService.columnMapping[property]);
    }

    hasVal(val) {
        return val === 0 || val > 0;
    }

    removeMappedField(colItem, dontRefresh) {
        if (colItem) {
            if (colItem.databaseField) {
                this.moveDatabaseFieldToUnusedList(colItem.databaseField);
            }
            colItem.databaseField = null;
            this.petlService.mappingsFromServerAltered = true;
        }
        if (!dontRefresh) {
            this.refreshColumnMapping();
        }
    }

    removeAllMappings() {
        this.removeAllMappedFields();
        this.refreshColumnMapping();
    }

    removeAllMappedFields() {
        this.petlService.mappingsFromServerAltered = true;
        this.petlService.columnData.forEach((colItem) => {
            this.removeMappedField(colItem, true);
        });
    }

    atLeastOneColumnIsMapped() {
        if (!this.petlService.columnData) {
            return false;
        }
        let columnMappedFound = false;
        this.petlService.columnData.forEach((columnDataValue) => {
            if (columnDataValue.databaseField) {
                columnMappedFound = true;
            }
        });
        return columnMappedFound;
    }

    getNumberOfColumnsMapped() {
        if (!this.petlService.columnData) {
            return 0;
        }
        let numberOfColumnsMapped = 0;
        this.petlService.columnData.forEach((columnDataValue) => {
            if (columnDataValue.databaseField) {
                numberOfColumnsMapped++;
            }
        });
        return numberOfColumnsMapped;
    }

    automapCustomFields() {
        this.petlService.mappingsFromServerAltered = true;
        this.petlService.columnData.forEach((columnDataValue) => {
            let autoAssignedDatabaseField = null;
            if (!columnDataValue.databaseField) {
                const dataType = this.calculateColumnDataType(columnDataValue);
                const dataTypeCustomFields = this.getCustomUnusedFields(dataType);

                if (dataTypeCustomFields.length > 0) {
                    autoAssignedDatabaseField = dataTypeCustomFields[0];
                } else {
                    const charCustomFields = this.getCustomUnusedFields('char');
                    if (charCustomFields.length > 0) {
                        autoAssignedDatabaseField = charCustomFields[0];
                    }
                }
                if (autoAssignedDatabaseField) {
                    columnDataValue.databaseField = autoAssignedDatabaseField;
                    if (columnDataValue.nativeHeader && this.petlService.restrictedAliases.indexOf(columnDataValue.nativeHeader) < 0) {
                        columnDataValue.databaseField.aliasName = columnDataValue.nativeHeader;
                    }
                    this.removeDatabaseFieldFromUnusedList(autoAssignedDatabaseField);
                }
            }
        });
        this.petlService.columnData = [].concat(this.petlService.columnData);
        this.refreshColumnMapping();
    }

    isNumeric(n) {
        return !isNaN(parseFloat(n)) && isFinite(n);
    }

    calculateColumnDataType(value) {
        let numbersFound = 0;
        let datesFound = 0;
        let urlsFound = 0;
        let blanksFound = 0;
        const thresholdPercentage = 75;
        const oneHundred = 100;
        const sampleCount = this.petlService.fileSample.length;

        this.petlService.fileSample.forEach((row) => {
            const cell = row[value.index];
            if (!cell) {
                blanksFound++;
            } else if (this.isNumeric(cell)) {
                numbersFound++;
            } else if (Date.parse(cell)) {
                datesFound++;
            } else if (cell.indexOf(':/') > 0 || cell.indexOf('http') === 0 || cell.indexOf('www.') === 0) {
                urlsFound++;
            }
        });
        if (numbersFound > 0 && (numbersFound * oneHundred / (sampleCount - blanksFound)) >= thresholdPercentage) {
            return 'num';
        } else if (datesFound > 0 && (datesFound * oneHundred / (sampleCount - blanksFound)) >= thresholdPercentage) {
            return 'date';
        } else if (urlsFound > 0 && (urlsFound * oneHundred / (sampleCount - blanksFound)) >= thresholdPercentage) {
            return 'url';
        }
        return 'char';
    }

    getCustomUnusedFields(dataType) {
        const customFields = this.petlService.results.unusedDatabaseFields.filter((el) => {
            return el.customField && el.databaseName.indexOf(`${dataType.toString()}_`) === 0;
        });
        (<any>customFields).sort((x, y) => {
            const indexOfNumberComponent = 2;
            x = parseInt(x.databaseName.split('_')[indexOfNumberComponent], 10);
            y = parseInt(y.databaseName.split('_')[indexOfNumberComponent], 10);
            if (x < y) {
                return -1;
            }
            if (x > y) {
                return 1;
            }
            return 0;
        });
        return customFields;
    }

    filterColumns(items: IDatabaseField[], filterProperty, filterValue) {
        const retVal: IDatabaseField[] = [];
        for (const item of items) {
            if (item[filterProperty] === undefined || item[filterProperty] === filterValue) {
                retVal.push(item);
            }
        }
        return retVal;
    }

    customFieldComparator(a: IDatabaseField, b: IDatabaseField) {
        const aVal = a.databaseName,
            bVal = b.databaseName,
            aNameIndex = this.nameOrderIndex(aVal),
            bNameIndex = this.nameOrderIndex(bVal);

        if (aNameIndex !== bNameIndex) {
            return (aNameIndex < bNameIndex) ? -1 : 1;
        }

        const aParts = aVal.split('_'),
            bParts = bVal.split('_'),
            aNum = aParts.length > 1 ? parseInt(aParts[aParts.length - 1], 10) : -1,
            bNum = bParts.length > 1 ? parseInt(bParts[bParts.length - 1], 10) : -1;

        if (isNaN(aNum) || isNaN(bNum)) {
            return !isNaN(aNum) ? -1 : 1;
        }

        return aNum === bNum ? 0 : (aNum < bNum ? -1 : 1);
    }

    nameOrderIndex(displayName: string) {
        for (const [i, value] of this.petlService.customTypes.entries()) {
            const index = displayName.trim().toLowerCase().indexOf(value);
            if (index === 0) {
                return i;
            }
        }
        return this.petlService.customTypes.length;
    }

    populateArray(max: number, base: number) {
        const retVal: number[] = [];
        base = base || 0;
        for (let i = base; i <= max; i++) {
            retVal.push(i);
        }
        return retVal;
    }

    getDragElementDisplayValue(columnData: IColumnResult) {
        if (columnData.databaseField) {
            const databaseField = columnData.databaseField;
            if (databaseField.aliasName) {
                return databaseField.aliasName;
            } else {
                return databaseField.displayName;
            }
        }
        return '';
    }

    dragOver(ev, dataitem: IColumnResult) {
        ev.preventDefault();
        if (dataitem) {
            if (this.draggedDatabaseField && this.draggedDatabaseField.customField) {
                dataitem.dragOverCustom = true;
            } else {
                dataitem.dragOver = true;
            }
        }
    }

    dragLeave(dataitem: IColumnResult) {
        dataitem.dragOverCustom = false;
        dataitem.dragOver = false;
    }

    allowDrop(ev) {
        ev.preventDefault();
    }

    drag(ev) {
        ev.dataTransfer.setData('ID', ev.target.id);
    }

    drop(dataitem) {
        if (dataitem) {
            if (this.draggedDatabaseField) {
                if (dataitem.databaseField) {
                    this.moveDatabaseFieldToUnusedList(dataitem.databaseField);
                }
                dataitem.databaseField = this.draggedDatabaseField;
                this.setColumnDataElementDroppedOn(dataitem);
            }
            dataitem.dragOverCustom = false;
            dataitem.dragOver = false;
        }
        this.cdr.detectChanges();
    }

    unusedDrop() {
        this.moveDatabaseFieldToUnusedList(this.draggedDatabaseField);
    }

    dragStart(dataitem) {
        this.setDraggedDatabaseField(dataitem.databaseField, dataitem);
    }

    unusedDragStart(databaseField: IDatabaseField) {
        this.setDraggedDatabaseField(databaseField, null);
    }

    dragEnd() {
        const timeout = 100;
        setTimeout(() => {
            this.setDraggedDatabaseField(null, null);
        }, timeout);
    }

}
